import { useMemo, useCallback } from "react";
import { useTheme } from "../hooks/use-theme";
import Animated, {
    Easing,
    ReduceMotion,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from "react-native-reanimated";
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import VStack from "../ui_atoms/VStack";
import { TitleMediumText } from "./FormattedText";
import { ThemeProvider } from "../contexts/ThemeManager";
import { useOnScreenFocused } from "../hooks/use-on-screen-focused";

// should be the same ast ui_atoms/Steps STEPS_DURATION
const STEPS_DURATION = 300;

type BubbleProps = React.PropsWithChildren<{
    title?: string;
    isAnimated?: boolean;
    isOpen?: boolean;
    isInitial?: boolean;
    origin?: [number, number]; // [x, y]
    style?: StyleProp<ViewStyle>;
    bubbleStyle?: StyleProp<ViewStyle>;
}>;

// isFocused = [false, true]
const opacityValues = [0, 1];

const AnimatedVStack = Animated.createAnimatedComponent(VStack);

export const BUBBLE_ANIMATION = {
    duration: STEPS_DURATION,
    easing: Easing.in(Easing.elastic(0.9)),
    reduceMotion: ReduceMotion.System,
};

export const Bubble = ({
    title,
    children,
    isInitial = false,
    isAnimated = true,
    origin,
    style,
    bubbleStyle,
}: BubbleProps) => {
    const styles = useStyles();
    const theme = useTheme();

    const opacityValue = useSharedValue(opacityValues[+isInitial]);

    // Opacity-only reveal (no scale). Keeps the transition a plain fade.
    const animatedStyles = useAnimatedStyle(() => ({
        opacity: opacityValue.value,
    }));

    const onScreenFocus = useCallback(
        (isFocused: boolean) => {
            // Only animate the bubble IN on focus. Do NOT fade it out on blur: with the
            // previous screen kept mounted underneath, the fade-out is never visible anyway —
            // but it would leave the bubble at opacity 0, making the screen look blank for
            // ~0.5s when navigating BACK to it.
            if (isAnimated && isFocused) {
                opacityValue.value = withTiming(opacityValues[1], BUBBLE_ANIMATION);
            }
        },
        [isAnimated, opacityValue],
    );

    useOnScreenFocused(onScreenFocus);

    const transformOrigin = useMemo(() => {
        if (origin && origin[0] !== undefined && origin[1] !== undefined) {
            return `${Math.round(origin[0])}px ${Math.round(origin[1])}px`;
        }
    }, [origin]);

    return (
        <AnimatedVStack
            style={[animatedStyles, style, transformOrigin && { marginTop: -6, transformOrigin }]}
        >
            {origin?.[0] !== undefined && (
                <View style={[styles.arrow, { left: origin[0] + ARROW_SIZE + 1 }]} />
            )}
            <VStack h="2/3" v="2/3" gap="2/3" style={[styles.bubble, bubbleStyle]}>
                {title ? <TitleMediumText>{title}</TitleMediumText> : null}
                <ThemeProvider
                    value={{
                        ...theme,
                        colors: {
                            ...theme.colors,
                            accentLighter: theme.colors.background,
                            background: theme.colors.accentLighter,
                            lighterGrey: theme.colors.background,
                        },
                    }}
                >
                    {children}
                </ThemeProvider>
            </VStack>
        </AnimatedVStack>
    );
};

const IMAGE_SIZE = 82;
const ARROW_SIZE = 14;

const useStyles = () => {
    const { colors, gridSize, roundness } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                image: {
                    width: IMAGE_SIZE,
                    height: IMAGE_SIZE,
                    borderRadius: IMAGE_SIZE / 2,
                    borderWidth: 4,
                    borderColor: colors.background,
                },
                arrow: {
                    // position: "absolute",
                    top: 8,
                    // top: -4,
                    // left: 34,
                    width: ARROW_SIZE,
                    height: ARROW_SIZE,
                    backgroundColor: colors.accentLighter,
                    transform: [{ rotate: "45deg" }],
                    borderRadius: roundness / 3,
                },
                bubble: {
                    overflow: "hidden",
                    marginHorizontal: gridSize / 2,
                    // transformOrigin: "50px 0px",
                    // paddingHorizontal: gridSize / 2,
                    // paddingVertical: gridSize / 3,
                    borderRadius: roundness * 2,
                    backgroundColor: colors.accentLighter,
                    // paddingBottom: 0,
                },
            }),
        [colors.accentLighter, colors.background, gridSize, roundness],
    );
};
